import axiosInstance from '@/setup/axios/axiosInstance';

import { RequestMaterialFormData, RequestMaterialLine, RequestMaterialListRow } from '../types';
import { getRequestMaterialStatuses } from './RequestMaterialStatusCRUD';

// ─── API shape ───────────────────────────────────────────────────────────────

interface ApiLine {
  id?: string;
  sort_order: number;
  line_type: 'item' | 'group';
  group_name?: string | null;
  item_id?: string | null;
  item_code?: string | null;
  item_name?: string;
  requested_qty?: number | null;
  uom_id?: string | null;
  uom_name?: string;
}

interface ApiRequest {
  id: string;
  request_no: string;
  request_type: 'order' | 'reserve';
  status: string;
  note: string;
  requested_at: string | null;
  required_by: string | null;
  requested_by_id?: string | null;
  requested_by_name?: string | null;
  company_id: string;
  company_name: string;
  order_id?: string | null;
  order_no?: string | null;
  warehouse_id?: string | null;
  warehouse_name?: string | null;
  created_by_name?: string;
  lines: ApiLine[];
}

interface ApiListResponse {
  results?: ApiRequest[];
  count?: number;
  page?: number;
  page_size?: number;
  total_pages?: number;
}

// ─── Mappers ──────────────────────────────────────────────────────────────────

const mapApiLineToLocal = (line: ApiLine, index: number): RequestMaterialLine => ({
  id: typeof line.id === 'string' ? parseInt(line.id.replace(/-/g, '').slice(0, 8), 16) : Date.now() + index,
  sort_order: line.sort_order,
  line_type: line.line_type,
  group_name: line.group_name || undefined,
  item_id: line.item_id || undefined,
  item_code: (line as any).item_code || (line as any).item?.code || undefined,
  item_name: line.item_name || undefined,
  requested_qty: line.requested_qty ?? undefined,
  uom_id: line.uom_id || undefined,
  uom_name: line.uom_name || undefined,
});

const mapApiToRow = (r: ApiRequest): RequestMaterialListRow => ({
  id: r.id,
  request_no: r.request_no,
  request_type: r.request_type,
  status: r.status,
  order_id: r.order_id || undefined,
  order_no: r.order_no || undefined,
  warehouse_id: r.warehouse_id || undefined,
  company_id: r.company_id,
  company_name: r.company_name,
  warehouse_name: r.warehouse_name || '',
  requested_at: r.requested_at || null,
  required_by: r.required_by || null,
  requested_by_id: r.requested_by_id || null,
  requested_by_name: r.requested_by_name || null,
  created_by: r.created_by_name || '',
  note: r.note || undefined,
  lines: (r.lines || []).map(mapApiLineToLocal),
});

const mapLineToPayload = (line: RequestMaterialLine) => ({
  sort_order: line.sort_order,
  line_type: line.line_type,
  group_name: line.group_name || null,
  item_id: line.item_id || null,
  requested_qty: line.requested_qty ?? null,
  uom_id: line.uom_id || null,
});

const resolveRequestMaterialStatusId = async (statusValue: string, companyId?: string) => {
  if (!statusValue) {
    return statusValue;
  }

  const statusOptions = await getRequestMaterialStatuses(companyId);
  const matchedStatus = statusOptions.find((option) => option.id === statusValue || option.code === statusValue);

  return matchedStatus?.id || statusValue;
};

const mapFormToPayload = async (form: RequestMaterialFormData) => ({
  request_type: form.request_type,
  status: await resolveRequestMaterialStatusId(form.status, form.company_id),
  company_id: form.company_id,
  warehouse_id: form.warehouse_id || null,
  order_id: form.request_type === 'order' ? (form.order_id || null) : null,
  note: form.note || '',
  requested_at: form.requested_at || null,
  required_by: form.required_by || null,
  requested_by_id: form.requested_by_id || null,
  lines: form.lines.map(mapLineToPayload),
});

// ─── API calls ────────────────────────────────────────────────────────────────

export interface GetMaterialRequestsParams {
  companyId?: string;
  search?: string;
  status?: string;
  requestType?: string;
  page?: number;
  pageSize?: number;
}

export async function getMaterialRequests(params: GetMaterialRequestsParams = {}): Promise<{
  results: RequestMaterialListRow[];
  count: number;
  totalPages: number;
}> {
  const query = new URLSearchParams();
  if (params.companyId) query.append('company', params.companyId);
  if (params.search) query.append('search', params.search);
  if (params.status && params.status !== 'all') query.append('status', params.status);
  if (params.requestType && params.requestType !== 'all') query.append('request_type', params.requestType);
  if (params.page) query.append('page', String(params.page));
  if (params.pageSize) query.append('page_size', String(params.pageSize));

  const qs = query.toString();
  const response = await axiosInstance.get<ApiListResponse | ApiRequest[]>(`/inventory/material-requests/${qs ? `?${qs}` : ''}`);
  const data = response.data;

  if (Array.isArray(data)) {
    return { results: data.map(mapApiToRow), count: data.length, totalPages: 1 };
  }
  const paged = data as ApiListResponse;
  return {
    results: (paged.results || []).map(mapApiToRow),
    count: paged.count || 0,
    totalPages: paged.total_pages || 1,
  };
}

export async function getMaterialRequest(id: string): Promise<RequestMaterialListRow> {
  const response = await axiosInstance.get<ApiRequest>(`/inventory/material-requests/${id}/`);
  return mapApiToRow(response.data);
}

export async function createMaterialRequest(form: RequestMaterialFormData): Promise<RequestMaterialListRow> {
  const payload = await mapFormToPayload(form);
  const response = await axiosInstance.post<ApiRequest>('/inventory/material-requests/', payload);
  return mapApiToRow(response.data);
}

export async function updateMaterialRequest(id: string, form: RequestMaterialFormData): Promise<RequestMaterialListRow> {
  const payload = await mapFormToPayload(form);
  const response = await axiosInstance.put<ApiRequest>(`/inventory/material-requests/${id}/`, payload);
  return mapApiToRow(response.data);
}

export async function submitMaterialRequest(id: string): Promise<RequestMaterialListRow> {
  const response = await axiosInstance.post<ApiRequest>(`/inventory/material-requests/${id}/submit/`);
  return mapApiToRow(response.data);
}

export async function approveMaterialRequest(id: string): Promise<RequestMaterialListRow> {
  const response = await axiosInstance.post<ApiRequest>(`/inventory/material-requests/${id}/approve/`);
  return mapApiToRow(response.data);
}

export async function cancelMaterialRequest(id: string): Promise<RequestMaterialListRow> {
  const response = await axiosInstance.post<ApiRequest>(`/inventory/material-requests/${id}/cancel/`);
  return mapApiToRow(response.data);
}

export async function deleteMaterialRequest(id: string): Promise<void> {
  await axiosInstance.delete(`/inventory/material-requests/${id}/`);
}
